/**
 * 「お金」の枠: 開発者アカウント単位の画面（売上・出金・受け取りの登録・前払い残高・利用料・パスキー）。
 *
 * WHY プロジェクトの枠（ProjectShell）の外に置くか: 売上の台帳・受取人・前払い残高はゲームごとではなく**クリエイターごと**
 * （apps/server/src/modules/money: developer_id 単位）。ゲームの画面に置くと「このゲームの残高」と誤解される。
 * ゲームの課金の画面（Payments）からは、ここへのリンクだけを出す。
 *
 * 本番 / テストの切り替え: 売上・出金・受け取りの登録には livemode がある（テスト = 開発環境の購入・Stripe のテスト）。
 * ProjectShell の「URL に環境を入れる」と同じ理由で、モードも URL に入れる（/money/live/… /money/test/…）= 共有したリンクで取り違えない。
 * 前払い残高・利用料・パスキーはモードを持たない（前払いは常に本物の残高・money/prepaid.ts）ので、切り替えを出さない。
 */
import { useState } from 'react';
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { useResource } from '../../hooks';
import { useT, type MessageKey } from '../../i18n';
import { money } from '../../money';
import type { PrepaidView } from '../../moneyApi';
import { TopBar } from '../ProjectsPage';
import { isMoneyNavHidden, moneyHomePath } from '../../launchHidden';
import { InviteCodeCard } from '../../components/InviteCodeCard';
import { ADS_PATH } from '../../selfAds';
import { SETTINGS_PATH as ADS_SETTINGS_PATH } from '../../selfAdsApi';
import { PLAY_EARN_API, PLAY_EARN_PATH } from '../../playEarn';

export type MoneyMode = 'live' | 'test';
export interface MoneyContext {
  mode: MoneyMode;
  livemode: boolean;
}
export const useMoney = () => useOutletContext<MoneyContext>();

const MODES: MoneyMode[] = ['live', 'test'];
const MODE_KEY = 'kumo.console.moneyMode';

/** 最後に見たモード（前払い残高の画面から「売上」に戻ったとき、さっきのモードに戻す）。保存できなければ live */
function rememberedMode(): MoneyMode {
  try {
    return localStorage.getItem(MODE_KEY) === 'test' ? 'test' : 'live';
  } catch {
    return 'live';
  }
}
function remember(mode: MoneyMode) {
  try {
    localStorage.setItem(MODE_KEY, mode);
  } catch {
    /* プライベートモード等。URL にモードがあるので困らない */
  }
}

interface NavItem {
  to: (m: MoneyMode) => string;
  label: MessageKey;
  icon: string;
  group?: MessageKey;
}

const NAV: NavItem[] = [
  // 「初日から稼げる」（2026-10-26 money/earn-build・playEarn.ts）。売上（payouts・最初の公開で見せない）とは別のお金の筋 = 先頭に置き、口がある時だけ出す（下の earnOn）
  { to: () => PLAY_EARN_PATH, label: 'pe.nav', icon: 'M12 2v20M17 6H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6', group: 'money.nav.group.earn' },
  // 見出し「稼ぐ」は先頭のプレイの稼ぎが持つ（出さない時は下の navItems が次の項目へ渡す）
  { to: (m) => `/money/${m}/earnings`, label: 'money.nav.earnings', icon: 'M4 19V5M4 19h16M8 15l3-4 3 2 5-6' },
  // 売上レポート（期間 × 切り口）と 3D セキュアの集計はモードを持つ（livemode の台帳・支払い）ので売上の群に置く（2026-09-27 money/dashboard-money2）
  { to: (m) => `/money/${m}/sales`, label: 'money.nav.sales', icon: 'M4 20h16M6 16V9M10 16V5M14 16v-4M18 16V8' },
  { to: (m) => `/money/${m}/three-d-secure`, label: 'money.nav.threeDSecure', icon: 'M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6zM9 12l2 2 4-4' },
  { to: (m) => `/money/${m}/withdraw`, label: 'money.nav.withdraw', icon: 'M12 4v12M6 10l6 6 6-6M4 20h16' },
  { to: (m) => `/money/${m}/get-paid`, label: 'money.nav.getPaid', icon: 'M3 10l9-6 9 6M5 10v8M9.5 10v8M14.5 10v8M19 10v8M3 20h18' },
  { to: () => '/money/prepaid', label: 'money.nav.prepaid', icon: 'M3 7h18v12H3zM3 7l3-3h12l3 3M16 13h2', group: 'money.nav.group.spend' },
  { to: () => '/money/usage', label: 'money.nav.usage', icon: 'M4 20V10h4v10M10 20V4h4v16M16 20v-7h4v7' },
  // 自分のゲームの広告（2026-10-22 money/self-serve-ads-dash）。クレジットから払う = 前払い・利用料と同じ群。口がある（フラグ on）ときだけ出す（下の adsOn）
  { to: () => ADS_PATH, label: 'ssa.nav', icon: 'M4 10v4h3l6 4V6L7 10zM16 9a4 4 0 0 1 0 6M18.5 6.5a8 8 0 0 1 0 11' },
  {
    to: () => '/money/passkeys',
    label: 'money.nav.passkeys',
    icon: 'M9 11a4 4 0 1 1 3.9-3H21v3h-2v2h-3v-2h-3.1A4 4 0 0 1 9 11zM7 7h.01',
    group: 'money.nav.group.security'
  }
];

/**
 * 最初の公開で見せない区切り（売上と出金 = payouts・launchHidden.ts の MONEY_NAV_FEATURES）を外したナビ（2026-09-29 creator/launch-money・Creator）。
 * 群の見出し（group）は群の先頭の項目が持っているので、先頭が隠れたら次に見える項目へ見出しを渡す。
 * ただし「売上」の群は 3D セキュア（前払いの入金の分）しか残らない = 「売上」の見出しは誤り → 利用料の群の最後へ移す（下の WHY）。
 * WHY 3D セキュアを利用料の群へ移すか: 残るのは前払いの入金（カード）の本人認証だけで、売上ではない。
 *   モードは本番（前払いは常に本物の残高 = 下の moneyNavTo）。一覧から payouts を外せば元の並びに戻る
 */
const EARN_HIDDEN = isMoneyNavHidden('earnings');
const VISIBLE_NAV: NavItem[] = (() => {
  const shown = NAV.filter((n) => !isMoneyNavHidden(sectionOf(n)));
  if (!EARN_HIDDEN) return shown;
  const tds = shown.filter((n) => sectionOf(n) === 'three-d-secure').map((n) => ({ ...n, group: undefined }));
  // プレイの稼ぎ（pe.nav）は売上を見せない間も「稼ぐ」の見出しのまま（本当に稼ぎの画面 = 3D セキュアとは違う）
  const rest = shown.filter((n) => sectionOf(n) !== 'three-d-secure').map((n) => (n.group === 'money.nav.group.earn' && n.label !== 'pe.nav' ? { ...n, group: undefined } : n));
  const lastSpend = rest.findLastIndex((n) => ['prepaid', 'usage'].includes(sectionOf(n)));
  return [...rest.slice(0, lastSpend + 1), ...tds, ...rest.slice(lastSpend + 1)];
})();

/** 出さない項目（off）を外し、外した項目の群の見出しを次の見出しの無い項目へ渡す（見出しだけ消えない・2 回出ない） */
function withCarriedGroups(items: Array<NavItem & { off?: boolean }>): NavItem[] {
  const out: NavItem[] = [];
  let carry: MessageKey | undefined;
  for (const n of items) {
    if (n.off) {
      carry = carry ?? n.group;
      continue;
    }
    out.push(carry && !n.group ? { ...n, group: carry } : n);
    carry = undefined;
  }
  return out;
}

/** ナビの項目の区切り（URL の末尾）。モードは何でもよい（末尾だけ見る） */
function sectionOf(n: NavItem): string {
  return n.to('live').split('/').at(-1) ?? '';
}

export function MoneyShell() {
  const { mode: modeParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useT();
  // 請求残の帯（どの画面にいても「止まっている」ことが分かるように）。前払い残高の画面は自分で詳しく出すので帯は出さない
  const prepaid = useResource<PrepaidView>('/v1/money/prepaid');
  // 自分のゲームの広告のナビを出すか。設定の口が 200 を返したときだけ（フラグ off = 404 で出さない・読み込み中も出さない = 一瞬出て消えない）
  const adsSettings = useResource<unknown>(ADS_SETTINGS_PATH);
  const adsOn = adsSettings.data !== null;
  // 「初日から稼げる」の稼ぎのナビ: GET /v1/money/earn が 200 の時だけ（見せない人 = 404・読み込み中も出さない）
  const earnView = useResource<unknown>(PLAY_EARN_API);
  const earnOn = earnView.data !== null;
  // 口が無い項目（広告・プレイの稼ぎ）を外す（withCarriedGroups）
  const navItems = withCarriedGroups(VISIBLE_NAV.map((n) => ((n.label === 'ssa.nav' && !adsOn) || (n.label === 'pe.nav' && !earnOn) ? { ...n, off: true } : n)));
  // 招待コードを入れた後に、前払い残高の画面（Money の PrepaidPage）を読み直させる印。WHY key で作り直すか: PrepaidPage の中を触らずに
  // 残高（招待の分を含む）を最新にできる（入口の枠だけで済む = 中身は Money の持ち物）
  const [refresh, setRefresh] = useState(0);

  if (modeParam !== undefined && !(MODES as string[]).includes(modeParam)) return <Navigate to={moneyHomePath()} replace />;
  const modeful = modeParam !== undefined;
  // 売上を見せない間はテストのモードを使わない（下の showModeSwitch の WHY）。古いリンクの /money/test/… は本番へ
  if (EARN_HIDDEN && modeParam === 'test') return <Navigate to={location.pathname.replace('/money/test/', '/money/live/')} replace />;
  const mode: MoneyMode = (modeParam as MoneyMode | undefined) ?? rememberedMode();
  if (modeful) remember(mode);
  const section = location.pathname.split('/').at(-1) ?? 'earnings';
  const switchMode = (next: MoneyMode) => {
    remember(next);
    navigate(`/money/${next}/${section}`);
  };
  const owed = prepaid.data?.owed ?? 0;
  // 売上を見せない間、モードの切り替え（本番 / テスト）は出さない: 残るモードつきの画面は 3D セキュア（前払いの入金）だけで、
  // 前払いは常に本物（テストの入金は無い）= 切り替えても空の画面になるだけ。ナビの行き先も本番に固定する（moneyNavTo）
  const showModeSwitch = modeful && !EARN_HIDDEN;
  const moneyNavTo = (n: NavItem) => n.to(EARN_HIDDEN ? 'live' : mode);

  return (
    <div className="page-wrap shell money-shell" data-mode={mode}>
      <a href="#main" className="skip-link">
        {t('common.skip')}
      </a>
      <TopBar />
      <div className="shell-body">
        <aside className="sidebar" aria-label={t('money.navLabel')}>
          <div className="sidebar-project">
            <span className="sidebar-label">{t('money.account')}</span>
            {/* 売上を見せない間は見出しも「利用料」だけ（2026-09-30 console/words-pass） */}
            <strong className="sidebar-name">{t(EARN_HIDDEN ? 'money.accountSubLaunch' : 'money.accountSub')}</strong>
          </div>

          {showModeSwitch && (
            <fieldset className="env-switch">
              <legend className="sidebar-label">{t('money.mode.legend')}</legend>
              {MODES.map((m) => (
                <label key={m} className={`env-option ${m === 'live' ? 'env-option-production' : 'env-option-development'} ${mode === m ? 'is-active' : ''}`}>
                  <input type="radio" name="money-mode" value={m} checked={mode === m} onChange={() => switchMode(m)} />
                  <span className="env-dot" aria-hidden="true" />
                  <span>{t(`money.mode.${m}`)}</span>
                </label>
              ))}
            </fieldset>
          )}

          <nav className="nav">
            {navItems.map((n) => (
              <div key={n.label} className="nav-item-wrap">
                {n.group && <span className="nav-group">{t(n.group)}</span>}
                <NavLink to={moneyNavTo(n)} className={({ isActive }) => `nav-link ${isActive ? 'is-active' : ''}`}>
                  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" className="nav-icon">
                    <path d={n.icon} />
                  </svg>
                  <span>{t(n.label)}</span>
                </NavLink>
              </div>
            ))}
          </nav>
        </aside>

        <div className="shell-main">
          {showModeSwitch && mode === 'test' && (
            <div className="env-band env-band-development" role="status">
              <span className="env-band-tag">{t('money.band.testTag')}</span>
              <span className="env-band-text">{t('money.band.test')}</span>
            </div>
          )}
          {owed > 0 && section !== 'prepaid' && (
            <div className="owed-band" role="alert">
              <span>{t('money.owedBanner', { owed: money(owed, prepaid.data?.currency ?? 'usd') })}</span>
              <Link to="/money/prepaid" className="btn btn-sm btn-danger">
                {t('money.owedBannerLink')}
              </Link>
            </div>
          )}
          <main className="container" id="main" tabIndex={-1}>
            <Outlet key={refresh} context={{ mode, livemode: mode === 'live' } satisfies MoneyContext} />
            {/* 招待コード（2026-09-29 creator/launch-money・Creator の部品）。前払い残高の画面の下に置く = 残高と同じ所で「うち招待クレジット」と入れる欄を見せる。
                WHY PrepaidPage の中に書かないか: 前払いの画面は Money の持ち物（中身は触らない）。置き場所は Money と相談の上で動かしてよい */}
            {section === 'prepaid' && (
              <InviteCodeCard
                onRedeemed={() => {
                  setRefresh((n) => n + 1);
                  prepaid.reload();
                }}
              />
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
